import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { ToastContainer, toast } from "react-toastify";
import {
  AccountTreeOutlinedIcon,
  BadgeOutlinedIcon,
  CloseRoundedIcon,
  DragIndicatorRoundedIcon,
  EmailOutlinedIcon,
  ErrorOutlineRoundedIcon,
  ExpandLessRoundedIcon,
  ExpandMoreRoundedIcon,
  GroupsOutlinedIcon,
  LocalPhoneOutlinedIcon,
  OpenInFullRoundedIcon,
  RemoveRoundedIcon,
  AddRoundedIcon,
  RestartAltRoundedIcon,
  SearchRoundedIcon,
  UnfoldLessRoundedIcon,
  UnfoldMoreRoundedIcon,
  WorkOutlineRoundedIcon,
} from "../../utilities/icons";
import {
  organizationHierarchyApi,
  organizationReassignApi,
  organizationValidateMoveApi,
} from "../../apiCalls/Apicalls";
import "./OrganizationHierarchy.css";

/**
 * @typedef {"Admin" | "HR" | "Manager" | "Team Lead" | "Employee"} RoleLevel
 * @typedef {{id: string, name: string, code?: string, roles?: string[]}} ProjectSummary
 * @typedef {{id: string, empId: string, name: string, email?: string, phone?: string, role: RoleLevel, dept?: string, jobTitle?: string, active?: boolean, managerId?: string, teamLeadId?: string, hrId?: string, reportingToId?: string, projects?: ProjectSummary[], projectIds?: string[], children?: HierarchyNode[]}} HierarchyNode
 * @typedef {{userId: string, targetParentId: string}} ReassignmentRequest
 */

const ROLE_LEVELS = ["Admin", "HR", "Manager", "Team Lead", "Employee"];
const VALID_PARENT_BY_ROLE = {
  HR: "Admin",
  Manager: "HR",
  "Team Lead": "Manager",
  Employee: "Team Lead",
};
const REASSIGNABLE_ROLES = new Set(["Employee", "Team Lead", "Manager"]);
const ROLE_CLASS = {
  Admin: "role-admin",
  HR: "role-hr",
  Manager: "role-manager",
  "Team Lead": "role-lead",
  Employee: "role-employee",
};
const ROLE_CONFIG = {
  Admin: { size: 52, fontSize: 15 },
  HR: { size: 48, fontSize: 14 },
  Manager: { size: 46, fontSize: 13 },
  "Team Lead": { size: 44, fontSize: 13 },
  Employee: { size: 42, fontSize: 12 },
};

function authHeaders() {
  return {
    Authorization: Cookies.get("jwtToken"),
    "Content-Type": "application/json",
  };
}

function initials(name = "") {
  const value = name.trim() || "User";
  return value
    .split(/\s+/)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function countDescendants(node) {
  const children = node?.children || [];
  return children.length + children.reduce((sum, child) => sum + countDescendants(child), 0);
}

function flattenTree(node) {
  if (!node) return [];
  return [node, ...(node.children || []).flatMap(flattenTree)];
}

function findNode(node, nodeId) {
  if (!node || !nodeId) return null;
  if (node.id === nodeId) return node;
  for (const child of node.children || []) {
    const found = findNode(child, nodeId);
    if (found) return found;
  }
  return null;
}

function isDescendant(source, targetId) {
  return (source?.children || []).some(
    (child) => child.id === targetId || isDescendant(child, targetId),
  );
}

function getParentName(tree, nodeId, parent = null) {
  if (!tree) return "None";
  if (tree.id === nodeId) return parent?.name || "None";
  for (const child of tree.children || []) {
    const found = getParentName(child, nodeId, tree);
    if (found !== "None") return found;
  }
  return "None";
}

function canDropNode(source, target) {
  if (!source || !target) return { valid: false, message: "Select a valid user" };
  if (source.id === target.id) {
    return { valid: false, message: "A user cannot report to themselves" };
  }
  if (source.role === "Admin") {
    return { valid: false, message: "Admin cannot be reassigned" };
  }
  if (!REASSIGNABLE_ROLES.has(source.role)) {
    return { valid: false, message: `${source.role} cannot be reassigned here` };
  }
  if (target.role === "Employee") {
    return { valid: false, message: "Employee cannot have direct reports" };
  }
  if (target.role !== VALID_PARENT_BY_ROLE[source.role]) {
    return {
      valid: false,
      message: `${source.role} must be moved under ${VALID_PARENT_BY_ROLE[source.role]}`,
    };
  }
  if (isDescendant(source, target.id)) {
    return { valid: false, message: "Cannot assign a user inside their own tree" };
  }
  return { valid: true, message: "Move is valid" };
}

function collectExpandedIds(node) {
  if (!node) return [];
  return [node.id, ...(node.children || []).flatMap(collectExpandedIds)];
}

function HierarchyNode({
  node,
  depth,
  expanded,
  highlightedIds,
  dimNonMatches,
  dragSourceId,
  dragOver,
  onToggle,
  onOpen,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDragLeave,
  onDrop,
}) {
  const hasChildren = Boolean(node.children?.length);
  const isExpanded = expanded.has(node.id);
  const cfg = ROLE_CONFIG[node.role] || ROLE_CONFIG.Employee;
  const descendants = countDescendants(node);
  const isHighlighted = highlightedIds.has(node.id);
  const isDim = dimNonMatches && !isHighlighted;
  const dragState =
    dragOver?.targetId === node.id ? (dragOver.valid ? " drag-over" : " drag-invalid") : "";

  return (
    <div className="org-node-wrap">
      <div
        className={`org-node-card ${ROLE_CLASS[node.role] || ""}${dragSourceId === node.id ? " dragging" : ""}${dragState}${isHighlighted ? " node-highlight" : ""}`}
        draggable={node.role !== "Admin"}
        style={{ opacity: isDim ? 0.35 : 1 }}
        onDragStart={(event) => onDragStart(event, node)}
        onDragEnd={onDragEnd}
        onDragOver={(event) => onDragOver(event, node)}
        onDragLeave={onDragLeave}
        onDrop={(event) => onDrop(event, node)}
      >
        <button
          type="button"
          className="org-node-inner"
          onClick={(event) => onOpen(node, event)}
        >
          <DragIndicatorRoundedIcon className="org-node-drag-handle" fontSize="inherit" />
          <span
            className="org-node-avatar"
            style={{ width: cfg.size, height: cfg.size, fontSize: cfg.fontSize }}
          >
            {initials(node.name)}
            <span
              className="org-node-status"
              style={{ background: node.active === false ? "#9ca3af" : "#22c55e" }}
            />
          </span>
          <span className="org-node-name">{node.name}</span>
          <span className="org-node-role">{node.role}</span>
          {descendants > 0 ? (
            <span className="org-node-count">
              <GroupsOutlinedIcon fontSize="inherit" /> {descendants} report
              {descendants === 1 ? "" : "s"}
            </span>
          ) : null}
        </button>

        {hasChildren ? (
          <button
            type="button"
            className="org-expand-btn"
            onClick={(event) => {
              event.stopPropagation();
              onToggle(node.id);
            }}
            title="Toggle children"
          >
            {isExpanded ? <ExpandLessRoundedIcon fontSize="inherit" /> : <ExpandMoreRoundedIcon fontSize="inherit" />}
          </button>
        ) : null}
      </div>

      {hasChildren && isExpanded ? (
        <>
          <div className="org-vertical-line" />
          <div className="org-children-area">
            {node.children.length > 1 ? <div className="org-horizontal-line" /> : null}
            <div className="org-children-row">
              {node.children.map((child) => (
                <HierarchyNode
                  key={child.id}
                  node={child}
                  depth={depth + 1}
                  expanded={expanded}
                  highlightedIds={highlightedIds}
                  dimNonMatches={dimNonMatches}
                  dragSourceId={dragSourceId}
                  dragOver={dragOver}
                  onToggle={onToggle}
                  onOpen={onOpen}
                  onDragStart={onDragStart}
                  onDragEnd={onDragEnd}
                  onDragOver={onDragOver}
                  onDragLeave={onDragLeave}
                  onDrop={onDrop}
                />
              ))}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}

function UserDetailsPopover({ node, position, parentName, onClose }) {
  if (!node) return null;
  const projectNames = (node.projects || []).map((project) => project.name).join(", ");

  return (
    <div className="org-popover-overlay open" onClick={onClose}>
      <div
        className={`org-popover ${ROLE_CLASS[node.role] || ""}`}
        style={{ left: position.left, top: position.top }}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="org-pop-header">
          <div className="org-pop-avatar">
            {initials(node.name)}
            <span
              className="org-pop-status"
              style={{ background: node.active === false ? "#9ca3af" : "#22c55e" }}
            />
          </div>
          <div className="org-pop-title">
            <div className="org-pop-name">{node.name}</div>
            <span className="org-pop-role-badge">{node.role}</span>
          </div>
          <button type="button" className="org-pop-close" onClick={onClose} aria-label="Close">
            <CloseRoundedIcon fontSize="inherit" />
          </button>
        </div>
        <div className="org-pop-body">
          <div className="org-pop-row">
            <BadgeOutlinedIcon />
            <span className="org-pop-row-label">Emp ID</span>
            <strong>{node.empId || node.id}</strong>
          </div>
          <div className="org-pop-row">
            <EmailOutlinedIcon />
            <span className="org-pop-row-label">Email</span>
            <strong>{node.email || "-"}</strong>
          </div>
          <div className="org-pop-row">
            <LocalPhoneOutlinedIcon />
            <span className="org-pop-row-label">Phone</span>
            <strong>{node.phone || "-"}</strong>
          </div>
          <div className="org-pop-row">
            <WorkOutlineRoundedIcon />
            <span className="org-pop-row-label">Dept</span>
            <strong>{node.dept || "-"}</strong>
          </div>
          <div className="org-pop-row">
            <AccountTreeOutlinedIcon />
            <span className="org-pop-row-label">Reports to</span>
            <strong>{parentName}</strong>
          </div>
          <div className="org-pop-row">
            <GroupsOutlinedIcon />
            <span className="org-pop-row-label">Projects</span>
            <strong>{projectNames || "-"}</strong>
          </div>
          <div className="org-pop-stats">
            <div className="org-pop-stat">
              <strong>{node.children?.length || 0}</strong>
              <span>Direct</span>
            </div>
            <div className="org-pop-stat">
              <strong>{countDescendants(node)}</strong>
              <span>Total</span>
            </div>
            <div className="org-pop-stat">
              <strong>{node.access?.length || 0}</strong>
              <span>Access</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ReassignConfirmModal({ pendingMove, saving, onCancel, onConfirm }) {
  if (!pendingMove) return null;

  return (
    <div className="org-modal-overlay open" onClick={saving ? undefined : onCancel}>
      <div className="org-modal" onClick={(event) => event.stopPropagation()}>
        <h3>Confirm Reassignment</h3>
        <p>
          Move <strong>{pendingMove.source.name}</strong> to report under{" "}
          <strong>{pendingMove.target.name}</strong> ({pendingMove.target.role})? This updates the reporting
          hierarchy for the selected user and keeps their direct reports with them.
        </p>
        <div className="org-modal-actions">
          <button type="button" className="org-btn-cancel" onClick={onCancel} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="org-btn-confirm" onClick={onConfirm} disabled={saving}>
            {saving ? "Saving..." : "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function OrganizationHierarchyPage() {
  const [tree, setTree] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(new Set());
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [deptFilter, setDeptFilter] = useState("");
  const [zoom, setZoom] = useState(1);
  const [fullscreen, setFullscreen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [popoverPosition, setPopoverPosition] = useState({ left: 0, top: 0 });
  const [dragSourceId, setDragSourceId] = useState("");
  const [dragOver, setDragOver] = useState(null);
  const [pendingMove, setPendingMove] = useState(null);
  const [savingMove, setSavingMove] = useState(false);

  const allNodes = useMemo(() => flattenTree(tree), [tree]);
  const departments = useMemo(
    () => [...new Set(allNodes.map((node) => node.dept).filter(Boolean))].sort(),
    [allNodes],
  );
  const roleCounts = stats?.roles || {};

  const fetchHierarchy = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const response = await axios.get(organizationHierarchyApi, {
        headers: authHeaders(),
      });
      const nextTree = response.data?.tree || null;
      setTree(nextTree);
      setStats(response.data?.stats || null);
      setExpanded(new Set(nextTree ? [nextTree.id, ...(nextTree.children || []).map((child) => child.id)] : []));
    } catch (fetchError) {
      console.error("Error fetching organization hierarchy:", fetchError);
      setError(fetchError.response?.data?.error || "Unable to load organization hierarchy.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHierarchy();
  }, [fetchHierarchy]);

  const highlightedIds = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term && !roleFilter && !deptFilter) return new Set();
    return new Set(
      allNodes
        .filter((node) => {
          const matchesSearch =
            !term ||
            `${node.name} ${node.role} ${node.empId} ${node.email} ${node.dept}`
              .toLowerCase()
              .includes(term);
          const matchesRole = !roleFilter || node.role === roleFilter;
          const matchesDept = !deptFilter || node.dept === deptFilter;
          return matchesSearch && matchesRole && matchesDept;
        })
        .map((node) => node.id),
    );
  }, [allNodes, deptFilter, roleFilter, search]);

  useEffect(() => {
    if (highlightedIds.size > 0 && tree) {
      setExpanded(new Set(collectExpandedIds(tree)));
    }
  }, [highlightedIds.size, tree]);

  const toggleExpand = (nodeId) => {
    setExpanded((previous) => {
      const next = new Set(previous);
      if (next.has(nodeId)) next.delete(nodeId);
      else next.add(nodeId);
      return next;
    });
  };

  const openPopover = (node, event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const width = 340;
    const height = 430;
    let left = rect.right + 12;
    let top = rect.top;
    if (left + width > window.innerWidth) left = rect.left - width - 12;
    if (top + height > window.innerHeight) top = window.innerHeight - height - 12;
    setPopoverPosition({ left: Math.max(8, left), top: Math.max(8, top) });
    setSelected(node);
  };

  const handleDragStart = (event, node) => {
    if (node.role === "Admin") {
      event.preventDefault();
      return;
    }
    setDragSourceId(node.id);
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", node.id);
  };

  const handleDragOver = (event, target) => {
    event.preventDefault();
    const source = findNode(tree, dragSourceId);
    const result = canDropNode(source, target);
    setDragOver({ targetId: target.id, valid: result.valid });
    event.dataTransfer.dropEffect = result.valid ? "move" : "none";
  };

  const handleDragEnd = () => {
    setDragSourceId("");
    setDragOver(null);
  };

  const handleDrop = async (event, target) => {
    event.preventDefault();
    const sourceId = event.dataTransfer.getData("text/plain") || dragSourceId;
    const source = findNode(tree, sourceId);
    const frontendValidation = canDropNode(source, target);
    setDragOver(null);
    setDragSourceId("");

    if (!frontendValidation.valid) {
      toast.error(frontendValidation.message);
      return;
    }

    try {
      const response = await axios.get(organizationValidateMoveApi, {
        headers: authHeaders(),
        params: { sourceId: source.id, targetId: target.id },
      });
      if (!response.data?.valid) {
        toast.error(response.data?.message || "Move is not valid");
        return;
      }
      setPendingMove({ source, target });
    } catch (validateError) {
      console.error("Error validating move:", validateError);
      toast.error(validateError.response?.data?.error || "Unable to validate move");
    }
  };

  const confirmReassign = async () => {
    if (!pendingMove || savingMove) return;
    try {
      setSavingMove(true);
      const response = await axios.put(
        organizationReassignApi,
        {
          userId: pendingMove.source.id,
          targetParentId: pendingMove.target.id,
        },
        { headers: authHeaders() },
      );
      setTree(response.data?.tree || null);
      setStats(response.data?.stats || null);
      setExpanded((previous) => new Set([...previous, pendingMove.target.id]));
      toast.success(`${pendingMove.source.name} reassigned to ${pendingMove.target.name}`);
      setPendingMove(null);
    } catch (moveError) {
      console.error("Error reassigning hierarchy:", moveError);
      toast.error(moveError.response?.data?.error || "Unable to save hierarchy change");
      fetchHierarchy();
    } finally {
      setSavingMove(false);
    }
  };

  const resetView = () => {
    setSearch("");
    setRoleFilter("");
    setDeptFilter("");
    setZoom(1);
    setExpanded(new Set(tree ? [tree.id, ...(tree.children || []).map((child) => child.id)] : []));
    toast.success("Hierarchy view reset");
  };

  const parentName = selected ? getParentName(tree, selected.id) : "None";
  const hasHighlights = highlightedIds.size > 0 || Boolean(search || roleFilter || deptFilter);

  return (
    <>
      <div className={`org-hierarchy${fullscreen ? " is-fullscreen" : ""}`}>
        <div className="org-toolbar">
          <button type="button" className="org-tb-btn" onClick={() => setExpanded(new Set(collectExpandedIds(tree)))}>
            <UnfoldMoreRoundedIcon fontSize="small" /> Expand All
          </button>
          <button
            type="button"
            className="org-tb-btn"
            onClick={() => setExpanded(new Set(tree ? [tree.id] : []))}
          >
            <UnfoldLessRoundedIcon fontSize="small" /> Collapse All
          </button>
          <div className="org-tb-sep" />
          <label className="org-search-box">
            <SearchRoundedIcon fontSize="small" />
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search employees..." />
          </label>
          <select className="org-filter-select" value={roleFilter} onChange={(event) => setRoleFilter(event.target.value)}>
            <option value="">All Roles</option>
            {ROLE_LEVELS.map((role) => (
              <option key={role} value={role}>
                {role}
              </option>
            ))}
          </select>
          <select className="org-filter-select" value={deptFilter} onChange={(event) => setDeptFilter(event.target.value)}>
            <option value="">All Departments</option>
            {departments.map((department) => (
              <option key={department} value={department}>
                {department}
              </option>
            ))}
          </select>
          <div className="org-tb-sep" />
          <button type="button" className="org-tb-btn" onClick={resetView}>
            <RestartAltRoundedIcon fontSize="small" /> Reset
          </button>
          <button type="button" className="org-tb-btn" onClick={() => setFullscreen((value) => !value)}>
            <OpenInFullRoundedIcon fontSize="small" /> {fullscreen ? "Exit" : "Fullscreen"}
          </button>
          <div className="org-tb-spacer" />
          <div className="org-zoom-ctrl">
            <button type="button" className="org-zoom-btn" onClick={() => setZoom((value) => Math.max(0.4, value - 0.1))}>
              <RemoveRoundedIcon fontSize="inherit" />
            </button>
            <span>{Math.round(zoom * 100)}%</span>
            <button type="button" className="org-zoom-btn" onClick={() => setZoom((value) => Math.min(1.5, value + 0.1))}>
              <AddRoundedIcon fontSize="inherit" />
            </button>
          </div>
        </div>

        <div className="org-breadcrumb">
          <button type="button" onClick={() => setSearch("")}>
            All
          </button>
          <span>/</span>
          <span>Full Hierarchy</span>
        </div>

        <div className="org-canvas-wrap">
          {loading ? (
            <div className="org-loading-state">
              <AccountTreeOutlinedIcon className="org-spin" />
              <span>Loading organization hierarchy...</span>
            </div>
          ) : error ? (
            <div className="org-empty-state">
              <ErrorOutlineRoundedIcon className="org-empty-icon" />
              <h3>Unable to load hierarchy</h3>
              <p>{error}</p>
              <button type="button" className="org-btn-confirm" onClick={fetchHierarchy}>
                Retry
              </button>
            </div>
          ) : tree ? (
            <div className="org-tree-stage">
              <div className="org-tree-canvas" style={{ zoom }}>
                <HierarchyNode
                  node={tree}
                  depth={0}
                  expanded={expanded}
                  highlightedIds={highlightedIds}
                  dimNonMatches={hasHighlights}
                  dragSourceId={dragSourceId}
                  dragOver={dragOver}
                  onToggle={toggleExpand}
                  onOpen={openPopover}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                  onDragOver={handleDragOver}
                  onDragLeave={() => setDragOver(null)}
                  onDrop={handleDrop}
                />
              </div>
            </div>
          ) : (
            <div className="org-empty-state">
              <GroupsOutlinedIcon className="org-empty-icon" />
              <h3>No users found</h3>
              <p>Add end users and assign hierarchy levels to build the reporting tree.</p>
            </div>
          )}
        </div>

        <div className="org-minimap">
          <div className="org-minimap-title">Overview</div>
          {["HR", "Manager", "Team Lead", "Employee"].map((role) => (
            <div className="org-minimap-row" key={role}>
              <span className={`org-minimap-dot ${ROLE_CLASS[role]}`} />
              <span>{role}</span>
              <strong>{roleCounts[role] || 0}</strong>
            </div>
          ))}
        </div>
      </div>

      <UserDetailsPopover node={selected} position={popoverPosition} parentName={parentName} onClose={() => setSelected(null)} />
      <ReassignConfirmModal
        pendingMove={pendingMove}
        saving={savingMove}
        onCancel={() => setPendingMove(null)}
        onConfirm={confirmReassign}
      />
      <ToastContainer />
    </>
  );
}
